//src/Components/Popups/useCreatePopup.tsx
import { compose } from '@reduxjs/toolkit';
import useBoolean from 'app/hooks/useBoolean';
import { uniqueId } from 'lodash';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { PopupManagerContext } from './context';
import {
  OpenPopupContext,
  PopupApi,
  PopupHandles,
  PopupState,
} from './Popup/type';
import { PopupStore } from './PopupManager';
import useCreatePopupProvider from './useCreatePopupProvider';

function usePopupManger() {
  return useContext(PopupManagerContext);
}

export function useCreatePopupApi<T>(hooks?: {
  onClose?: () => void;
  onOpen?: () => void;
}): {
  api: PopupHandles<T>;
  state: PopupState<T>;
} {
  const {
    value: isOpened,
    setTrue: open,
    setFalse,
    toggle,
  } = useBoolean(false);

  const forceClose = () => {
    hooks && hooks.onClose && hooks.onClose();
    setFalse();
  };

  const {
    value: isLocked,
    setTrue: lock,
    setFalse: unlock,
    toggle: toggleLock,
  } = useBoolean(false);

  const {
    value: backdrop,
    setTrue: enableBackdrop,
    setFalse: disableBackdrop,
    toggle: toggleBackDrop,
  } = useBoolean(true);

  const close = () => {
    !isLocked && forceClose();
    return !isLocked;
  };

  const isClosed = !isOpened;
  // const openIfClose = () => isClosed && open();
  // const closeIfOpen = () => isOpened && close();

  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [popupRoot, setPopupRoot] = useState<HTMLElement | null>(null);
  const [contextData, setContextData] = useState<T>();

  const setContext = ({ data, anchor }: OpenPopupContext<T>) => {
    setContextData(data);
    anchor && setAnchor(anchor);
  };
  const openWithContext = compose(open, setContext);

  const toggleWithContext = compose(toggle, setContext);

  const state = {
    isOpened,
    isClosed,
    isLocked,
    backdrop,
    contextData,
    anchor,
    popupRoot,
  };

  const api = useMemo(
    () => ({
      // handles
      open,
      close,
      forceOpen: open,
      forceClose,
      toggle,
      openWithContext,
      setContext,
      setContextData,
      toggleWithContext,

      lock,
      unlock,
      toggleLock,

      enableBackdrop,
      disableBackdrop,
      toggleBackDrop,

      setAnchor,
      setPopupRoot,
    }),
    [],
  );
  return { state, api };
}

function useRegisterPopup(api: PopupApi<any>, name = '') {
  const [id] = useState(() => uniqueId());

  useEffect(() => {
    if (api.isOpened) {
      PopupStore.unregister(id);
      PopupStore.register({ id, api, name });
    } else {
      PopupStore.unregister(id);
    }
  }, [api.isOpened, api.lock, api.backdrop]);

  useEffect(() => {
    return () => PopupStore.unregister(id);
  }, []);
  return id;
}

export function useCreatePopup<T>(options?: {
  name?: string;
  type?: 'modal' | 'dropdown';
  hooks?: {
    onClose?: () => void;
  };
}) {
  const { state, api } = useCreatePopupApi<T>(options?.hooks);
  const popupApi = { ...api, ...state };

  const popupId = useRegisterPopup(popupApi);
  const Component = useCreatePopupProvider(
    popupApi,
    popupId,
    options?.type || 'dropdown',
  );

  return useMemo(() => ({ Component, ...api }), []);
}
